import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Send, X, Phone, MessageSquare, Loader2, User, Bot, Shield } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

interface WidgetConfig {
  primary_color: string;
  secondary_color: string;
  position: 'bottom-right' | 'bottom-left';
  bubble_size: number;
  avatar_url?: string;
  welcome_message: string;
  offline_message: string;
  header_text: string;
}

interface ConsentType {
  key: string;
  label: string;
  required: boolean;
  description: string;
}

interface PrivacyDoc {
  title: string;
  content: string;
  consent_types: ConsentType[];
  version: number;
}

interface ChatbotConfig {
  id: string;
  name: string;
  description?: string;
  widget_config: WidgetConfig;
  quick_replies: Array<{ text: string; action?: string }>;
  click_to_call_enabled: boolean;
  click_to_call_config: {
    mode: 'webrtc' | 'callback';
  };
  lead_capture_enabled: boolean;
  privacy?: PrivacyDoc | null;
  tenant_slug?: string | null;
}

const defaultConfig: WidgetConfig = {
  primary_color: '#3b82f6',
  secondary_color: '#1e40af',
  position: 'bottom-right',
  bubble_size: 60,
  welcome_message: 'Ciao! Come posso aiutarti?',
  offline_message: 'Al momento non siamo disponibili.',
  header_text: 'Assistente Virtuale'
};

export default function ChatbotWidget() {
  const { agentId } = useParams<{ agentId: string }>();
  const [config, setConfig] = useState<ChatbotConfig | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callbackName, setCallbackName] = useState('');
  const [callbackStatus, setCallbackStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadStatus, setLeadStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadConsents, setLeadConsents] = useState<Record<string, boolean>>({});
  const [callbackConsents, setCallbackConsents] = useState<Record<string, boolean>>({});
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const callbackFormRef = useRef<HTMLDivElement>(null);
  const leadFormRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const widgetConfig = config?.widget_config || defaultConfig;
  const privacyDoc = config?.privacy || null;
  const consentTypes = privacyDoc?.consent_types || [];

  const startNewSession = useCallback(() => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(`chatbot_session_${agentId}`, newSessionId);
    setSessionId(newSessionId);
    sessionStorage.removeItem(`chatbot_messages_${agentId}`);
    setMessages([]);
    return newSessionId;
  }, [agentId]);

  useEffect(() => {
    startNewSession();

    const alreadyCaptured = localStorage.getItem(`chatbot_lead_captured_${agentId}`);
    if (alreadyCaptured) {
      setLeadCaptured(true);
    }
  }, [agentId, startNewSession]);

  const getApiBaseUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const customBase = searchParams.get('api_base');
    if (customBase) return customBase;
    if (window.location.origin.includes('localhost')) return 'http://localhost:3000';
    return window.location.origin;
  };

  const loadConfig = useCallback(async () => {
    if (!agentId) return;
    
    setIsConfigLoading(true);
    try {
      const baseUrl = getApiBaseUrl();
      
      const response = await fetch(`${baseUrl}/api/chatbot/public/${agentId}/config`);
      const data = await response.json();
      
      if (data.success) {
        setConfig(data.data);
        const welcomeMessage = data.data.widget_config?.welcome_message;
        if (welcomeMessage) {
          setMessages(prev => prev.length === 0 ? [{
            id: 'welcome',
            role: 'assistant',
            content: welcomeMessage,
            timestamp: new Date()
          }] : prev);
        }
      } else {
        setError(data.error || 'Chatbot non trovato');
      }
    } catch (err) {
      console.error('Error loading config:', err);
      setError('Errore di connessione');
    } finally {
      setIsConfigLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadConfig();
  }, [agentId, loadConfig]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(`chatbot_messages_${agentId}`, JSON.stringify(messages));
    }
  }, [messages, agentId]);

  useEffect(() => {
    if (showCallbackForm) {
      setTimeout(() => {
        if (callbackFormRef.current) {
          callbackFormRef.current.scrollTop = callbackFormRef.current.scrollHeight;
        }
      }, 150);
    }
  }, [showCallbackForm]);

  useEffect(() => {
    if (showLeadForm) {
      setTimeout(() => {
        if (leadFormRef.current) {
          leadFormRef.current.scrollTop = leadFormRef.current.scrollHeight;
        }
      }, 150);
    }
  }, [showLeadForm]);

  const sendMessage = async (text?: string) => {
    const messageText = text || inputValue.trim();
    if (!messageText || isLoading || !agentId) return;

    const userMessage: Message = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const baseUrl = getApiBaseUrl();

      const conversationHistory = messages
        .filter(m => m.role !== 'system' && m.id !== 'welcome')
        .map(m => ({ role: m.role, content: m.content }));

      const assistantMsgId = `assistant_${Date.now()}`;

      const response = await fetch(`${baseUrl}/api/chatbot/public/${agentId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId,
          conversation_history: conversationHistory,
          visitor_info: {
            page_url: window.location.href,
            referrer: document.referrer
          },
          stream: true
        })
      });

      if (response.headers.get('content-type')?.includes('text/event-stream')) {
        const placeholderMessage: Message = {
          id: assistantMsgId,
          role: 'assistant',
          content: '',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, placeholderMessage]);

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed || !trimmed.startsWith('data: ')) continue;
              const jsonStr = trimmed.slice(6);
              if (jsonStr === '[DONE]') continue;

              try {
                const parsed = JSON.parse(jsonStr);
                if (parsed.type === 'text' && parsed.text) {
                  setMessages(prev => prev.map(m =>
                    m.id === assistantMsgId
                      ? { ...m, content: m.content + parsed.text }
                      : m
                  ));
                } else if (parsed.type === 'model_fallback') {
                  console.warn(`[Chatbot] Model fallback: ${parsed.original_model} -> ${parsed.fallback_model}`);
                } else if (parsed.type === 'done') {
                  if (parsed.session_id && parsed.session_id !== sessionId) {
                    setSessionId(parsed.session_id);
                    sessionStorage.setItem(`chatbot_session_${agentId}`, parsed.session_id);
                  }
                } else if (parsed.type === 'error') {
                  setMessages(prev => prev.map(m =>
                    m.id === assistantMsgId
                      ? { ...m, content: 'Mi scuso, si è verificato un errore. Riprova più tardi.' }
                      : m
                  ));
                }
              } catch {}
            }
          }
        }
      } else {
        const data = await response.json();

        if (data.success) {
          const assistantMessage: Message = {
            id: assistantMsgId,
            role: 'assistant',
            content: data.response,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, assistantMessage]);

          if (data.session_id && data.session_id !== sessionId) {
            setSessionId(data.session_id);
            sessionStorage.setItem(`chatbot_session_${agentId}`, data.session_id);
          }
        } else {
          const errorMessage: Message = {
            id: `error_${Date.now()}`,
            role: 'assistant',
            content: 'Mi scuso, si è verificato un errore. Riprova più tardi.',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: 'Errore di connessione. Verifica la tua connessione internet.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const requiredConsentsGranted = (consents: Record<string, boolean>) => {
    if (consentTypes.length === 0) return true;
    return consentTypes.filter(ct => ct.required).every(ct => consents[ct.key]);
  };

  const buildGdprConsents = (consents: Record<string, boolean>) => {
    return consentTypes.map(ct => ({ key: ct.key, label: ct.label, granted: !!consents[ct.key] }));
  };

  const sendCallbackRequest = async () => {
    if (!callbackPhone.trim() || !agentId) return;
    if (consentTypes.length > 0 && !requiredConsentsGranted(callbackConsents)) return;
    setCallbackStatus('sending');
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/chatbot/public/${agentId}/click-to-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone: callbackPhone.trim(), 
          name: callbackName.trim(),
          gdpr_consents: consentTypes.length > 0 ? buildGdprConsents(callbackConsents) : undefined
        })
      });
      const data = await response.json();
      if (data.success) {
        setCallbackStatus('sent');
        setTimeout(() => {
          setShowCallbackForm(false);
          setCallbackStatus('idle');
          setCallbackPhone('');
          setCallbackName('');
          setCallbackConsents({});
        }, 3000);
      } else {
        setCallbackStatus('error');
        setTimeout(() => setCallbackStatus('idle'), 3000);
      }
    } catch (err) {
      console.error('Error sending callback request:', err);
      setCallbackStatus('error');
      setTimeout(() => setCallbackStatus('idle'), 3000);
    }
  };

  const sendLeadCapture = async () => {
    if ((!leadPhone.trim() && !leadEmail.trim()) || !agentId) return;
    if (consentTypes.length > 0 && !requiredConsentsGranted(leadConsents)) return;
    setLeadStatus('sending');
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/chatbot/public/${agentId}/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: leadName.trim(),
          email: leadEmail.trim(),
          phone: leadPhone.trim(),
          session_id: sessionId,
          gdpr_consents: consentTypes.length > 0 ? buildGdprConsents(leadConsents) : undefined
        })
      });
      const data = await response.json();
      if (data.success) {
        setLeadStatus('sent');
        setLeadCaptured(true);
        localStorage.setItem(`chatbot_lead_captured_${agentId}`, 'true');
        setTimeout(() => {
          setShowLeadForm(false);
          setLeadStatus('idle');
          setLeadConsents({});
        }, 3000);
      } else {
        setLeadStatus('error');
        setTimeout(() => setLeadStatus('idle'), 3000);
      }
    } catch (err) {
      console.error('Error capturing lead:', err);
      setLeadStatus('error');
      setTimeout(() => setLeadStatus('idle'), 3000);
    }
  };

  const handleClose = () => {
    window.parent.postMessage('edg-chatbot-close', '*');
    setIsOpen(false);
  };

  const clearHistory = () => {
    startNewSession();
    if (config?.widget_config?.welcome_message) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: config.widget_config.welcome_message,
        timestamp: new Date()
      }]);
    }
  };

  if (isConfigLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-gray-900 text-white p-4">
        <MessageSquare className="h-12 w-12 text-red-400 mb-4" />
        <p className="text-lg font-medium">{error}</p>
        <p className="text-sm text-gray-400 mt-2">Verifica l'ID del chatbot e riprova</p>
      </div>
    );
  }

  return (
    <div 
      className="h-screen w-full flex flex-col bg-gray-900 text-white overflow-hidden"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
    >
      <div 
        className="flex items-center justify-between px-4 py-3 shrink-0"
        style={{ background: `linear-gradient(135deg, ${widgetConfig.primary_color}, ${widgetConfig.secondary_color})` }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{widgetConfig.header_text}</h3>
            <p className="text-xs text-white/70">Solitamente risponde subito</p>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X className="h-5 w-5 text-white" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                message.role === 'user'
                  ? 'bg-blue-500 text-white rounded-br-md'
                  : message.role === 'system'
                  ? 'bg-amber-700 text-amber-100 rounded-bl-md text-xs italic'
                  : 'bg-gray-800 text-gray-100 rounded-bl-md'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              <p className="text-xs opacity-50 mt-1">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {config?.quick_replies && config.quick_replies.length > 0 && messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {config.quick_replies.map((reply, idx) => (
            <button
              key={idx}
              onClick={() => sendMessage(reply.text)}
              className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-full text-sm text-gray-300 transition-colors border border-gray-700"
            >
              {reply.text}
            </button>
          ))}
        </div>
      )}

      <div className="p-4 border-t border-gray-800 shrink-0">
        {config?.click_to_call_enabled && !showCallbackForm && (
          <button
            className="w-full mb-3 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors text-white"
            style={{ backgroundColor: widgetConfig.primary_color }}
            onClick={() => setShowCallbackForm(true)}
          >
            <Phone className="h-4 w-4" />
            Richiedi una chiamata
          </button>
        )}
        {showCallbackForm && (
          <div ref={callbackFormRef} className="mb-3 p-3 rounded-lg bg-gray-800 border border-gray-700 space-y-2 overflow-y-auto max-h-52">
            {callbackStatus === 'sent' ? (
              <div className="text-center py-2">
                <div className="text-green-400 text-sm font-medium">Richiesta inviata!</div>
                <div className="text-gray-400 text-xs mt-1">Ti richiameremo al più presto</div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 font-medium">Richiedi una richiamata</span>
                  <button onClick={() => { setShowCallbackForm(false); setCallbackStatus('idle'); }} className="text-gray-500 hover:text-gray-300">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Il tuo nome (opzionale)"
                  value={callbackName}
                  onChange={(e) => setCallbackName(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="tel"
                  placeholder="Il tuo numero di telefono *"
                  value={callbackPhone}
                  onChange={(e) => setCallbackPhone(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  required
                />
                {consentTypes.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    {consentTypes.map(ct => (
                      <label key={ct.key} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!callbackConsents[ct.key]}
                          onChange={(e) => setCallbackConsents(prev => ({ ...prev, [ct.key]: e.target.checked }))}
                          className="mt-0.5 rounded border-gray-600 bg-gray-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="text-xs text-gray-400 leading-tight">
                          {ct.description || ct.label}
                          {ct.required && <span className="text-red-400 ml-0.5">*</span>}
                        </span>
                      </label>
                    ))}
                    {privacyDoc && (
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-xs underline flex items-center gap-1 mt-1"
                        style={{ color: widgetConfig.primary_color }}
                      >
                        <Shield className="h-3 w-3" />
                        {privacyDoc.title}
                      </button>
                    )}
                  </div>
                )}
                {callbackStatus === 'error' && (
                  <div className="text-red-400 text-xs">Errore nell'invio. Riprova.</div>
                )}
                <button
                  onClick={sendCallbackRequest}
                  disabled={!callbackPhone.trim() || callbackStatus === 'sending' || (consentTypes.length > 0 && !requiredConsentsGranted(callbackConsents))}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: widgetConfig.primary_color }}
                >
                  {callbackStatus === 'sending' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Phone className="h-4 w-4" />
                  )}
                  {callbackStatus === 'sending' ? 'Invio...' : 'Invia richiesta'}
                </button>
              </>
            )}
          </div>
        )}
        
        {config?.lead_capture_enabled && !leadCaptured && !showLeadForm && messages.filter(m => m.role === 'user').length >= 2 && (
          <button
            className="w-full mb-3 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-600 text-gray-300 hover:bg-gray-800"
            onClick={() => setShowLeadForm(true)}
          >
            <User className="h-4 w-4" />
            Lascia i tuoi dati per essere ricontattato
          </button>
        )}
        {showLeadForm && !leadCaptured && (
          <div ref={leadFormRef} className="mb-3 p-3 rounded-lg bg-gray-800 border border-gray-700 space-y-2 overflow-y-auto max-h-52">
            {leadStatus === 'sent' ? (
              <div className="text-center py-2">
                <div className="text-green-400 text-sm font-medium">Dati inviati con successo!</div>
                <div className="text-gray-400 text-xs mt-1">Ti contatteremo al più presto</div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 font-medium">Lascia i tuoi contatti</span>
                  <button onClick={() => { setShowLeadForm(false); setLeadStatus('idle'); }} className="text-gray-500 hover:text-gray-300">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Nome e cognome"
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="tel"
                  placeholder="Telefono"
                  value={leadPhone}
                  onChange={(e) => setLeadPhone(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-500">* Inserisci almeno email o telefono</p>
                {consentTypes.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    {consentTypes.map(ct => (
                      <label key={ct.key} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!leadConsents[ct.key]}
                          onChange={(e) => setLeadConsents(prev => ({ ...prev, [ct.key]: e.target.checked }))}
                          className="mt-0.5 rounded border-gray-600 bg-gray-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="text-xs text-gray-400 leading-tight">
                          {ct.description || ct.label}
                          {ct.required && <span className="text-red-400 ml-0.5">*</span>}
                        </span>
                      </label>
                    ))}
                    {privacyDoc && (
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-xs underline flex items-center gap-1 mt-1"
                        style={{ color: widgetConfig.primary_color }}
                      >
                        <Shield className="h-3 w-3" />
                        {privacyDoc.title}
                      </button>
                    )}
                  </div>
                )}
                {leadStatus === 'error' && (
                  <div className="text-red-400 text-xs">Errore nell'invio. Riprova.</div>
                )}
                <button
                  onClick={sendLeadCapture}
                  disabled={(!leadPhone.trim() && !leadEmail.trim()) || leadStatus === 'sending' || (consentTypes.length > 0 && !requiredConsentsGranted(leadConsents))}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: widgetConfig.primary_color }}
                >
                  {leadStatus === 'sending' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  {leadStatus === 'sending' ? 'Invio...' : 'Invia i miei dati'}
                </button>
              </>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Scrivi un messaggio..."
            className="flex-1 bg-gray-800 border border-gray-700 rounded-full px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            disabled={isLoading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isLoading}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: widgetConfig.primary_color }}
          >
            <Send className="h-5 w-5 text-white" />
          </button>
        </div>
        
        <p className="text-center text-xs text-gray-600 mt-3">
          Powered by EDG VoIP
        </p>
      </div>

      {showPrivacyModal && privacyDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" style={{ color: widgetConfig.primary_color }} />
                <h3 className="font-semibold text-white text-sm">{privacyDoc.title}</h3>
              </div>
              <button onClick={() => setShowPrivacyModal(false)} className="text-gray-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-sm text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: privacyDoc.content }} />
            <div className="p-3 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: widgetConfig.primary_color }}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
